import React, { useState, useEffect } from 'react';
import { Users, UserPlus, UserMinus, UserCheck, Plus, Send, Check, X, AlertCircle, Clock, ChevronDown, ChevronUp, Mail, Trash2 } from 'lucide-react';
import { apiGet, apiPost, apiDelete, apiPatch } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const REQUEST_STATUS_STYLES = {
  pending: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  approved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  denied: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const SeatManagementPanel = ({ subscriptionId, isAdmin = false }) => {
  const { currentUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [requestHistory, setRequestHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // Assign modal
  const [showAssign, setShowAssign] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Purchase/request modal
  const [showAddSeats, setShowAddSeats] = useState(false);
  const [addQuantity, setAddQuantity] = useState(1);
  const [addReason, setAddReason] = useState('');
  const [addingSeats, setAddingSeats] = useState(false);

  // Invite by email (individual subscriptions)
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [invitations, setInvitations] = useState([]);

  useEffect(() => {
    if (subscriptionId) {
      fetchSeats();
      fetchRequestHistory();
      fetchInvitations();
    }
  }, [subscriptionId]);

  const base = `/billing/subscriptions/${subscriptionId}/seats`;
  const adminBase = `/admin/billing/subscriptions/${subscriptionId}`;

  const fetchSeats = async () => {
    try {
      setLoading(true);
      const response = await apiGet(base);
      if (!response.ok) throw new Error('Failed to fetch seats');
      setData(await response.json());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignableUsers = async () => {
    try {
      const response = await apiGet(`${base}/assignable_users`);
      if (response.ok) setAssignableUsers(await response.json());
    } catch (err) { /* ignore */ }
  };

  const fetchRequestHistory = async () => {
    try {
      const response = await apiGet(`${base}/requests`);
      if (response.ok) setRequestHistory(await response.json());
    } catch (err) { /* ignore */ }
  };

  const fetchInvitations = async () => {
    try {
      const response = await apiGet(`${base}/invitations`);
      if (response.ok) setInvitations(await response.json());
    } catch (err) { /* ignore */ }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      setInviting(true);
      const response = await apiPost(`${base}/invite`, { email: inviteEmail.trim() });
      if (!response.ok) {
        const d = await response.json();
        throw new Error(d.error || 'Failed to send invitation');
      }
      setSuccess('Invitation sent');
      setTimeout(() => setSuccess(null), 3000);
      setInviteEmail('');
      fetchInvitations();
      fetchSeats();
    } catch (err) {
      setError(err.message);
    } finally {
      setInviting(false);
    }
  };

  const handleRevokeInvitation = async (invitationId) => {
    try {
      const response = await apiDelete(`${base}/invitations/${invitationId}`);
      if (!response.ok) throw new Error('Failed to revoke');
      setSuccess('Invitation revoked');
      setTimeout(() => setSuccess(null), 3000);
      fetchInvitations();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAssign = async () => {
    if (!selectedUserId) return;
    try {
      setAssigning(true);
      const response = await apiPost(`${base}/assign`, { user_id: selectedUserId });
      if (!response.ok) {
        const d = await response.json();
        throw new Error(d.error || 'Failed to assign seat');
      }
      setSuccess('Seat assigned');
      setTimeout(() => setSuccess(null), 3000);
      setShowAssign(false);
      setSelectedUserId('');
      fetchSeats();
    } catch (err) {
      setError(err.message);
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignSelf = async () => {
    if (!currentUser?.id) return;
    try {
      setAssigning(true);
      const response = await apiPost(`${base}/assign`, { user_id: currentUser.id });
      if (!response.ok) {
        const d = await response.json().catch(() => ({}));
        throw new Error(d.error || 'Failed to assign seat');
      }
      setSuccess('Seat assigned to you');
      setTimeout(() => setSuccess(null), 3000);
      fetchSeats();
    } catch (err) {
      setError(err.message);
    } finally {
      setAssigning(false);
    }
  };

  const handleRemove = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName}'s seat? They will lose access to this subscription's features.`)) return;
    try {
      const response = await apiDelete(`${base}/${userId}`);
      if (!response.ok) throw new Error('Failed to remove seat');
      setSuccess('Seat removed');
      setTimeout(() => setSuccess(null), 3000);
      fetchSeats();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddSeats = async () => {
    try {
      setAddingSeats(true);
      let response;
      if (data.can_purchase) {
        response = await apiPost(`${base}/purchase`, { quantity: addQuantity });
      } else if (isAdmin) {
        response = await apiPatch(`${adminBase}/add_seats`, { quantity: addQuantity });
      } else {
        response = await apiPost(`${base}/request`, { quantity: addQuantity, reason: addReason });
      }
      if (!response.ok) {
        const d = await response.json();
        throw new Error(d.error || 'Failed');
      }
      const action = data.can_purchase ? 'Seats purchased' : isAdmin ? 'Seats added' : 'Seat request submitted';
      setSuccess(action);
      setTimeout(() => setSuccess(null), 3000);
      setShowAddSeats(false);
      setAddQuantity(1);
      setAddReason('');
      fetchSeats();
      fetchRequestHistory();
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingSeats(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!data) return null;

  // Calculate per-seat price using volume tiers if available
  const getPerSeatPrice = (quantity) => {
    const tiers = data.seat_discount_tiers || [];
    if (tiers.length > 0) {
      const tier = tiers.find(t => quantity >= (t.min_seats || 1) && (!t.max_seats || quantity <= t.max_seats));
      if (tier) return (tier.price_monthly_cents || 0) / 100;
    }
    return data.per_seat_price_monthly ? (data.per_seat_price_monthly / 100) : 0;
  };
  const perSeatPrice = getPerSeatPrice(addQuantity);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-blue-500" />
          <h4 className="font-unna font-semibold dark:text-white">Seat Management</h4>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-rasa font-medium ${data.seats_available > 0 ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>
            {data.seats_used}/{data.total_seats} seats used
          </span>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 p-2.5 rounded-lg border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm mb-3 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)}><X size={14} /></button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 p-2.5 rounded-lg border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 text-sm mb-3 flex items-center gap-1">
          <Check size={14} /> {success}
        </div>
      )}

      {/* Seat capacity bar */}
      <div className="mb-4">
        <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2.5">
          <div
            className={`h-2.5 rounded-full transition-all ${data.seats_available > 0 ? 'bg-blue-500' : 'bg-orange-500'}`}
            style={{ width: `${Math.min(100, (data.seats_used / data.total_seats) * 100)}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-400 dark:text-gray-500 font-rasa mt-1">
          <span>{data.included_seats} included + {data.additional_seats_purchased} purchased</span>
          <span>{data.seats_available} available</span>
        </div>
      </div>

      {/* Seated users list */}
      {data.seats?.length > 0 ? (
        <div className="space-y-2 mb-4">
          {data.seats.map(seat => (
            <div key={seat.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div>
                <span className="text-sm font-rasa dark:text-white">{seat.user.name || seat.user.email}</span>
                {seat.user.name && <span className="text-xs text-gray-400 ml-2">{seat.user.email}</span>}
              </div>
              <button
                onClick={() => handleRemove(seat.user.id, seat.user.name || seat.user.email)}
                className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                title="Remove seat"
              >
                <UserMinus size={16} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-400 dark:text-gray-500 font-rasa mb-4">No seats assigned yet</p>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {data.seats_available > 0 && (
          <button
            onClick={() => { setShowAssign(true); fetchAssignableUsers(); }}
            className="flex items-center gap-1.5 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-rasa"
          >
            <UserPlus size={16} /> Assign Seat
          </button>
        )}
        {data.seats_available > 0 && currentUser?.email &&
          !(data.seats || []).some(s => s.user?.email?.toLowerCase() === currentUser.email.toLowerCase()) && (
            <button
              onClick={handleAssignSelf}
              disabled={assigning}
              className="flex items-center gap-1.5 px-3 py-2 border border-blue-500 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 text-sm font-rasa disabled:opacity-50"
            >
              <UserCheck size={16} /> {assigning ? 'Assigning…' : 'Assign to me'}
            </button>
          )}
        <button
          onClick={() => setShowAddSeats(true)}
          className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-rasa dark:text-gray-300"
        >
          <Plus size={16} />
          {data.can_purchase ? 'Purchase Seats' : isAdmin ? 'Add Seats' : 'Request Seats'}
        </button>
      </div>

      {/* Invite by email (individual subscriptions — no org member pool) */}
      {data.seats_available > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
          <p className="text-sm font-rasa font-medium dark:text-white mb-2 flex items-center gap-1.5">
            <Mail size={14} className="text-blue-500" />
            Share a seat by email
          </p>
          <div className="flex gap-2">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
              placeholder="Enter email address"
              className="flex-1 px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm font-rasa"
            />
            <button
              onClick={handleInvite}
              disabled={inviting || !inviteEmail.trim()}
              className="flex items-center gap-1.5 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 text-sm font-rasa"
            >
              <Send size={14} />
              {inviting ? 'Sending...' : 'Invite'}
            </button>
          </div>
        </div>
      )}

      {/* Pending invitations */}
      {invitations.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-unna uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Invitations</p>
          <div className="space-y-2">
            {invitations.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div>
                  <span className="text-sm font-rasa dark:text-white">{inv.email}</span>
                  <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full font-rasa ${
                    inv.status === 'pending' && !inv.expired
                      ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                      : inv.status === 'accepted'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-600 dark:text-gray-400'
                  }`}>
                    {inv.expired && inv.status === 'pending' ? 'expired' : inv.status}
                  </span>
                </div>
                {inv.status === 'pending' && !inv.expired && (
                  <button
                    onClick={() => handleRevokeInvitation(inv.id)}
                    className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    title="Revoke invitation"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Request History */}
      {requestHistory.length > 0 && (
        <div className="mt-4">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 font-rasa"
          >
            {showHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            Seat Requests ({requestHistory.filter(r => r.status === 'pending').length} pending, {requestHistory.length} total)
          </button>
          {showHistory && (
            <div className="mt-2 space-y-2">
              {requestHistory.map(req => (
                <div key={req.id} className="flex items-start justify-between py-2 px-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-rasa ${REQUEST_STATUS_STYLES[req.status]}`}>
                        {req.status}
                      </span>
                      <span className="text-sm font-rasa dark:text-white">
                        {req.seats_requested} seat{req.seats_requested > 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 dark:text-gray-500 font-rasa space-y-0.5">
                      <p className="flex items-center gap-1">
                        <Clock size={10} /> {new Date(req.created_at).toLocaleDateString()}
                        {req.requested_by && ` by ${req.requested_by.name || req.requested_by.email}`}
                      </p>
                      {req.reason && <p>Reason: {req.reason}</p>}
                      {req.admin_notes && <p className="text-blue-400">Admin: {req.admin_notes}</p>}
                      {req.resolved_by && (
                        <p>{req.status === 'approved' ? 'Approved' : 'Denied'} by {req.resolved_by.email} on {new Date(req.resolved_at).toLocaleDateString()}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Assign Modal */}
      {showAssign && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAssign(false)}>
          <div className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-unna font-semibold dark:text-white">Assign Seat</h3>
              <button onClick={() => setShowAssign(false)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"><X size={18} className="dark:text-gray-300" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-rasa font-medium mb-1 dark:text-gray-300">Select User</label>
                <select
                  value={selectedUserId}
                  onChange={e => setSelectedUserId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm font-rasa"
                >
                  <option value="">Choose a user...</option>
                  {assignableUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.name || u.email}</option>
                  ))}
                </select>
                {assignableUsers.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1 font-rasa">No available users to assign. All org members may already have seats.</p>
                )}
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setShowAssign(false)} className="px-4 py-2 text-sm font-rasa text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
                <button onClick={handleAssign} disabled={!selectedUserId || assigning} className="px-4 py-2 text-sm font-rasa bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50">
                  {assigning ? 'Assigning...' : 'Assign'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Seats Modal */}
      {showAddSeats && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddSeats(false)}>
          <div className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-unna font-semibold dark:text-white">
                {data.can_purchase ? 'Purchase Seats' : isAdmin ? 'Add Seats' : 'Request Seats'}
              </h3>
              <button onClick={() => setShowAddSeats(false)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"><X size={18} className="dark:text-gray-300" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-rasa font-medium mb-1 dark:text-gray-300">Number of seats</label>
                <input
                  type="number"
                  min="1"
                  value={addQuantity}
                  onChange={e => setAddQuantity(parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                />
              </div>
              {data.can_purchase && perSeatPrice > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-sm font-rasa">
                  <p className="text-blue-700 dark:text-blue-400">
                    {addQuantity} seat{addQuantity > 1 ? 's' : ''} × ${perSeatPrice.toFixed(2)}/mo = <strong>${(perSeatPrice * addQuantity).toFixed(2)}/mo</strong>
                  </p>
                  {data.seat_discount_tiers?.length > 0 && (
                    <div className="text-blue-500 text-xs mt-2 space-y-0.5">
                      <p className="font-medium">Volume pricing tiers:</p>
                      {data.seat_discount_tiers.map((tier, i) => (
                        <p key={i} className={addQuantity >= (tier.min_seats || 1) && (!tier.max_seats || addQuantity <= tier.max_seats) ? 'font-bold' : ''}>
                          {tier.min_seats || 1}-{tier.max_seats || '+'} seats: ${((tier.price_monthly_cents || 0) / 100).toFixed(2)}/seat
                          {addQuantity >= (tier.min_seats || 1) && (!tier.max_seats || addQuantity <= tier.max_seats) ? ' (current)' : ''}
                        </p>
                      ))}
                    </div>
                  )}
                  <p className="text-blue-500 text-xs mt-1">Prorated for the current billing period</p>
                </div>
              )}
              {!data.can_purchase && !isAdmin && (
                <div>
                  <label className="block text-sm font-rasa font-medium mb-1 dark:text-gray-300">Reason (optional)</label>
                  <textarea
                    value={addReason}
                    onChange={e => setAddReason(e.target.value)}
                    rows={2}
                    placeholder="Why do you need more seats?"
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-1 font-rasa flex items-center gap-1">
                    <AlertCircle size={12} /> Your request will be sent to an admin for approval
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button onClick={() => setShowAddSeats(false)} className="px-4 py-2 text-sm font-rasa text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
                <button onClick={handleAddSeats} disabled={addingSeats || addQuantity < 1} className="px-4 py-2 text-sm font-rasa bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50">
                  {addingSeats ? 'Processing...' : data.can_purchase ? `Purchase ${addQuantity} Seat${addQuantity > 1 ? 's' : ''}` : isAdmin ? 'Add Seats' : 'Submit Request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeatManagementPanel;
